import { useMemo } from 'react'
import LoadingBox from 'components/Common/Loading/LoadingBox'
import useMerchandiseCategory from 'hooks/Merchandise/useMerchandiseCategory'
import useMerchandiseTileTracking from 'hooks/Merchandise/useMerchandiseTileTracking'
import { rem } from 'polished'
import styled from 'styled-components'
import { getMerchandiseCategoryPageUrl, getMerchandiseCategorySearchUrl } from '../merchandiseUtils'
import Image from 'components/Common/Image'
import BodyText from 'LuxKit/Typography/BodyText'
import Clickable from 'components/Common/Clickable'
import { mediaQueryUp } from 'components/utils/breakpoint'
import Group from 'components/utils/Group'

const CategoryTile = styled(Clickable)`
  display: grid;
  gap: ${rem(16)};
  grid-template-columns: ${rem(85)} 1fr;
  width: ${rem(280)};

  ${mediaQueryUp.tablet} {
    grid-template-columns: 1fr;
    grid-template-rows: ${rem(180)} 1fr;
    flex-direction: column;
    grid-gap: ${rem(12)};
    width: ${rem(180)};
  }
`

const ImageContainer = styled.div`
  width: ${rem(85)};
  height: ${rem(85)};

  ${mediaQueryUp.tablet} {
    width: ${rem(180)};
    height: ${rem(180)};
  }
`

interface Props {
  id: string
  index?: number
}

function MerchandiseCategoryCard(props: Props) {
  const { id, index = 0 } = props
  const [category] = useMerchandiseCategory({ id })
  const { setTileRef, onClick } = useMerchandiseTileTracking('category', category, index)

  const linkTarget = useMemo(() => {
    if (!category) {
      return
    }

    return category.level > 1 ? getMerchandiseCategorySearchUrl(category) : getMerchandiseCategoryPageUrl(category)
  }, [category])

  if (!category) {
    return <LoadingBox height={180} width={170} />
  }

  return <CategoryTile to={linkTarget} onClick={onClick} ref={setTileRef}>
    <ImageContainer>
      <Image
        fit="center"
        width={180}
        dpr={3}
        image={category.image}
      />
    </ImageContainer>
    <Group direction="vertical" gap={4} horizontalAlign="start" verticalAlign="center" tabletVerticalAlign="start">
      <BodyText variant="large" weight="bold">{category.name}</BodyText>
    </Group>
  </CategoryTile>
}

export default MerchandiseCategoryCard
